import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Color } from "@faire/web/slate/Color";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveProp } from "@faire/web/slate/Theme/ResponsiveProp";
import { Typography, TypographyVariants } from "@faire/web/slate/Typography";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { type JSX } from "react";
import styled from "styled-components";

import { EyebrowText } from "./common";
import { ITestimonialsData } from "./TestinomialsData";

interface ITestimonialProps {
  testimonial: ITestimonialsData;
  title: JSX.Element;
  titleColor?: Color;
  textColor: Color;
  textStyle?: ResponsiveProp<TypographyVariants>;
}

const TestimonialCardBase = ({
  testimonial,
  title,
  textColor,
  titleColor,
  textStyle,
}: ITestimonialProps) => {
  const isSSR = useIsSSR();
  const viewport = useViewport();
  const { width, height } = useResponsiveValue({
    mobileAndAbove: { width: 227, height: 295 },
    desktopAndAbove: { width: 401, height: 521 },
  });
  return (
    <Wrapper>
      <InfoWrapper>
        <div>
          {viewport.isTabletAndAbove ? (
            <>
              <EyebrowText $color={titleColor ? titleColor : textColor}>
                {title}
              </EyebrowText>
              <Spacer height={viewport.isTablet ? "3x" : "5x"} />
            </>
          ) : null}
          <Typography
            color={textColor}
            variant={
              textStyle
                ? textStyle
                : {
                    mobileAndAbove: "pageHeaderSerifRegular",
                    desktopAndAbove: "displaySSerifRegular",
                  }
            }
          >
            {testimonial.text}
          </Typography>
        </div>
        {viewport.isMobile ? <Spacer size="3x" /> : null}
        <div>
          <Typography
            color={textColor}
            variant={{
              mobileAndAbove: "subheadingSansMedium",
              desktopAndAbove: "sectionHeaderSansMedium",
            }}
          >
            {testimonial.name}
          </Typography>
          <Spacer height="1x" />
          <Typography
            color={textColor}
            variant={{
              mobileAndAbove: "paragraphSansRegular",
              desktopAndAbove: "subheadingSansRegular",
            }}
          >
            {testimonial.info}
          </Typography>
        </div>
      </InfoWrapper>
      <Spacer
        size={{
          mobileAndAbove: "3x",
          tabletAndAbove: "4x",
          desktopAndAbove: "9x",
          xLargeAndAbove: "10x",
          xxLargeDesktop: 115,
        }}
      />
      <Image
        src={optimizeImageUrl({ url: testimonial.imageUrl }, isSSR, {
          width,
          height,
          resizeMethod: "fit",
        })}
      />
    </Wrapper>
  );
};

export const TestimonialCard = observer(TestimonialCardBase);

const Wrapper = styled(Flex).attrs({
  align: { mobileAndAbove: "center", tabletAndAbove: "stretch" },
  direction: { mobileAndAbove: "column-reverse", tabletAndAbove: "row" },
})`
  max-width: 1310px;
  margin: 0 auto;
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: auto;
  }
`;

const InfoWrapper = styled(Column).attrs({
  justify: { mobileAndAbove: "normal", tabletAndAbove: "space-between" },
})``;

const Image = styled.img`
  width: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: auto;
    height: 295px;
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    height: 521px;
  }
`;
